// P10 sheets (frozen mockups-adv-v1): SCR-ADV-01 family and variant, SCR-ADV-02 bundle, SCR-ADV-03 lot tracking,
// SCR-ADV-04 the Lots tab, SCR-ADV-05 record an expired lot. The server decides; these checks only answer at once.
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  BottomSheet,
  Button,
  Chips,
  EmptyState,
  Field,
  FormGrid,
  Input,
  LineAdd,
  List,
  ListRow,
  Notice,
  Section,
  Select,
  SheetActions,
  SkeletonList,
  SummaryCard,
} from '../../design/kit';
import {
  createFamily,
  createVariant,
  loadBundleAvailability,
  loadFamilies,
  loadLots,
  loadProductPlaces,
  optInLots,
  recordExpiredLot,
  saveBundle,
  type Family,
  type Lot,
} from '../../data/advanced';
import type { Brand, Category, Product, Unit } from '../../data/catalog';
import { Code, Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { formatDate } from '../../lib/format';
import { useSession } from '../../app/session';
import { CATALOG, parseAmount, parseWhole, refusalCode, useRefusalText } from './common';

export const FAMILIES = [...CATALOG, 'families'] as const;
export const LOTS = [...CATALOG, 'lots'] as const;

export function useFamilies() {
  return useQuery({ queryKey: FAMILIES, queryFn: loadFamilies });
}
export function useBundleAvailability() {
  return useQuery({ queryKey: [...CATALOG, 'bundle-availability'], queryFn: loadBundleAvailability });
}

/** SCR-ADV-01: a family with one to three attributes and their values (owner, ADR-122). */
export function FamilySheet({
  categories,
  brands,
  onClose,
  onSaved,
}: {
  categories: Category[];
  brands: Brand[];
  onClose: () => void;
  onSaved: (family: string) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [attrs, setAttrs] = useState<{ name: string; values: string[] }[]>([]);
  const [attrName, setAttrName] = useState('');
  const [attrValues, setAttrValues] = useState('');
  const [problem, setProblem] = useState('');
  const save = useMutation({
    mutationFn: () =>
      createFamily({ name: name.trim(), category: category || null, brand: brand || null, attributes: attrs }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: CATALOG });
      onSaved(r.id);
    },
    onError: (e) => setProblem(refusal(e, 'err.famname')),
  });
  const addAttr = () => {
    const values = [
      ...new Set(
        attrValues
          .split(/[,،]/)
          .map((v) => v.trim())
          .filter(Boolean),
      ),
    ];
    if (attrs.length >= 3) return setProblem(t('err.attrmax'));
    if (!attrName.trim()) return setProblem(t('err.required'));
    if (values.length === 0) return setProblem(t('err.attrvalue'));
    setAttrs([...attrs, { name: attrName.trim(), values }]);
    setAttrName('');
    setAttrValues('');
    setProblem('');
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setProblem(t('err.required'));
    if (attrs.length === 0) return setProblem(t('err.attrnone'));
    setProblem('');
    save.mutate();
  };
  return (
    <BottomSheet title={t('newfamily')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        <Field label={t('fam.name')}>
          {({ id }) => <Input id={id} dir="auto" value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <FormGrid>
          <Field label={t('category')}>
            {({ id }) => (
              <Select id={id} value={category} onChange={(e) => setCategory(e.target.value)}>
                <option value="">{t('none')}</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.path}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('brand')}>
            {({ id }) => (
              <Select id={id} value={brand} onChange={(e) => setBrand(e.target.value)}>
                <option value="">{t('none')}</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </FormGrid>
        <p className="muted">{t('fam.inherit')}</p>
        {attrs.length > 0 && (
          <Section title={t('fam.attrs')}>
            <List label={t('fam.attrs')} animateNew>
              {attrs.map((a, i) => (
                <ListRow
                  key={a.name}
                  testId={`attr-${i}`}
                  title={<Name>{a.name}</Name>}
                  sub={
                    <span className="yv-chips">
                      {a.values.map((v) => (
                        <span key={v} className="yv-chip">
                          <Name>{v}</Name>
                        </span>
                      ))}
                    </span>
                  }
                  actions={
                    <Button
                      pill
                      icon="close"
                      aria-label={t('remove')}
                      onClick={() => setAttrs(attrs.filter((_, j) => j !== i))}
                    />
                  }
                />
              ))}
            </List>
          </Section>
        )}
        {attrs.length < 3 && (
          <LineAdd>
            <FormGrid>
              <Field label={t('fam.attr')}>
                {({ id }) => (
                  <Input id={id} dir="auto" value={attrName} onChange={(e) => setAttrName(e.target.value)} />
                )}
              </Field>
              <Field label={t('fam.values')} hint={t('fam.valueshint')}>
                {({ id, describedBy }) => (
                  <Input
                    id={id}
                    dir="auto"
                    value={attrValues}
                    aria-describedby={describedBy}
                    onChange={(e) => setAttrValues(e.target.value)}
                  />
                )}
              </Field>
            </FormGrid>
            <Button icon="plus" onClick={addAttr}>
              {t('fam.addattr')}
            </Button>
          </LineAdd>
        )}
        {problem && <Notice tone="danger">{problem}</Notice>}
        <SheetActions>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('fam.save')}
          </Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-ADV-01: a variant — one value per attribute; the name is built from the family (PRD-07). */
export function VariantSheet({
  families,
  family: initial,
  onClose,
  onSaved,
}: {
  families: Family[];
  family: string;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const [familyId, setFamilyId] = useState(initial);
  const family = families.find((f) => f.id === familyId);
  const [values, setValues] = useState<string[]>(() => (family?.attributes ?? []).map((a) => a.values[0] ?? ''));
  const [sku, setSku] = useState('');
  const [units, setUnits] = useState<Unit[]>(['piece']);
  const [capacity, setCapacity] = useState('');
  const [piecePrice, setPiecePrice] = useState('');
  const [boxPrice, setBoxPrice] = useState('');
  const [problem, setProblem] = useState('');
  // The family may arrive after the sheet opens (just created): each attribute falls back to its first value.
  const vals = (family?.attributes ?? []).map((a, i) => values[i] ?? a.values[0] ?? '');
  const built = family ? [family.name, ...vals].join(' · ') : '';
  const save = useMutation({
    mutationFn: () =>
      createVariant({
        family: familyId,
        values: vals,
        sku: sku.trim() || null,
        capacity: capacity.trim() ? Number(capacity) : null,
        sellPiece: units.includes('piece'),
        sellBox: units.includes('box'),
        piecePrice: parseAmount(piecePrice),
        boxPrice: parseAmount(boxPrice),
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: CATALOG });
      onSaved(r.id);
    },
    onError: (e) => setProblem(refusal(e)),
  });
  const pickFamily = (id: string) => {
    setFamilyId(id);
    setValues((families.find((f) => f.id === id)?.attributes ?? []).map((a) => a.values[0] ?? ''));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const cap = parseWhole(capacity);
    if (units.length === 0) return setProblem(t('err.unit'));
    if ((units.includes('box') || cap !== null) && (cap === null || Number.isNaN(cap) || cap < 2))
      return setProblem(t('err.capacity'));
    if (
      (units.includes('piece') && !(parseAmount(piecePrice) ?? 0)) ||
      (units.includes('box') && !(parseAmount(boxPrice) ?? 0))
    )
      return setProblem(t('err.price'));
    setProblem('');
    save.mutate();
  };
  return (
    <BottomSheet title={t('newvariant')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        <Field label={t('f.family')}>
          {({ id }) => (
            <Select id={id} value={familyId} onChange={(e) => pickFamily(e.target.value)}>
              {families.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <FormGrid>
          {(family?.attributes ?? []).map((a, i) => (
            <Field key={a.position} label={a.name}>
              {({ id }) => (
                <Select
                  id={id}
                  value={vals[i] ?? ''}
                  onChange={(e) => setValues(vals.map((v, j) => (j === i ? e.target.value : v)))}
                >
                  {a.values.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ))}
        </FormGrid>
        <Field label={t('fam.builtname')}>{({ id }) => <Input id={id} dir="auto" readOnly value={built} />}</Field>
        <Field label={t('sku')} hint={t('sku.hint')}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              dir="ltr"
              value={sku}
              aria-describedby={describedBy}
              onChange={(e) => setSku(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('units')}>
          {() => (
            <Chips
              label={t('units')}
              items={[
                { id: 'piece', label: t('unit.piece') },
                { id: 'box', label: t('unit.box') },
              ]}
              selected={units}
              onToggle={(id) =>
                setUnits((cur) => (cur.includes(id as Unit) ? cur.filter((x) => x !== id) : [...cur, id as Unit]))
              }
            />
          )}
        </Field>
        <Field label={t('capacity')} hint={t('capacity.hint')}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              numeric
              inputMode="numeric"
              value={capacity}
              aria-describedby={describedBy}
              onChange={(e) => setCapacity(e.target.value)}
            />
          )}
        </Field>
        <FormGrid>
          {units.includes('piece') && (
            <Field label={t('pieceprice')}>
              {({ id }) => (
                <Input
                  id={id}
                  numeric
                  inputMode="decimal"
                  value={piecePrice}
                  onChange={(e) => setPiecePrice(e.target.value)}
                />
              )}
            </Field>
          )}
          {units.includes('box') && (
            <Field label={t('boxprice')}>
              {({ id }) => (
                <Input
                  id={id}
                  numeric
                  inputMode="decimal"
                  value={boxPrice}
                  onChange={(e) => setBoxPrice(e.target.value)}
                />
              )}
            </Field>
          )}
        </FormGrid>
        {problem && <Notice tone="danger">{problem}</Notice>}
        <SheetActions>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('var.save')}
          </Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-ADV-02: a bundle — a piece price, no stock, two or more components in whole pieces (PRD-08, ADR-121). */
export function BundleSheet({
  products,
  onClose,
  onSaved,
}: {
  products: Product[];
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [price, setPrice] = useState('');
  const [parts, setParts] = useState<{ product: string; quantity: number }[]>([]);
  const [product, setProduct] = useState('');
  const [quantity, setQuantity] = useState('');
  const [problem, setProblem] = useState('');
  const choices = products.filter((p) => p.active && !p.is_bundle && !parts.some((x) => x.product === p.id));
  const nameOf = (id: string) => products.find((p) => p.id === id)?.name ?? '';
  const save = useMutation({
    mutationFn: () =>
      saveBundle({ name: name.trim(), sku: sku.trim() || null, price: parseAmount(price), components: parts }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: CATALOG });
      onSaved(r.id);
    },
    onError: (e) => setProblem(refusal(e, 'err.prodname')),
  });
  const add = () => {
    const q = parseWhole(quantity);
    if (!product) return setProblem(t('err.required'));
    if (q === null || Number.isNaN(q) || q <= 0) return setProblem(t('err.bunqty'));
    setParts([...parts, { product, quantity: q }]);
    setProduct('');
    setQuantity('');
    setProblem('');
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setProblem(t('err.required'));
    if (!(parseAmount(price) ?? 0)) return setProblem(t('err.bunprice'));
    if (parts.length < 2) return setProblem(t('err.bun2'));
    setProblem('');
    save.mutate();
  };
  return (
    <BottomSheet title={t('newbundle')} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        <Field label={t('name')}>
          {({ id }) => <Input id={id} dir="auto" value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <FormGrid>
          <Field label={t('sku')}>
            {({ id }) => <Input id={id} dir="ltr" value={sku} onChange={(e) => setSku(e.target.value)} />}
          </Field>
          <Field label={t('bun.piece')}>
            {({ id }) => (
              <Input id={id} numeric inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
            )}
          </Field>
        </FormGrid>
        {parts.length > 0 && (
          <Section title={t('bun.comps')}>
            <List label={t('bun.comps')} animateNew>
              {parts.map((c, i) => (
                <ListRow
                  key={c.product}
                  testId={`comp-${i}`}
                  title={<Name>{nameOf(c.product)}</Name>}
                  sub={`${t('bun.per')}: ${c.quantity}`}
                  actions={
                    <Button
                      pill
                      icon="close"
                      aria-label={t('remove')}
                      onClick={() => setParts(parts.filter((_, j) => j !== i))}
                    />
                  }
                />
              ))}
            </List>
          </Section>
        )}
        <LineAdd>
          <Field label={t('product')}>
            {({ id }) => (
              <Select id={id} value={product} onChange={(e) => setProduct(e.target.value)}>
                <option value="">{t('choose')}</option>
                {choices.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('bun.per')}>
            {({ id }) => (
              <Input
                id={id}
                numeric
                inputMode="numeric"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            )}
          </Field>
          <Button icon="plus" onClick={add}>
            {t('line.add')}
          </Button>
        </LineAdd>
        <Notice tone="info">{t('bun.nostock')}</Notice>
        {problem && <Notice tone="danger">{problem}</Notice>}
        <SheetActions>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('bun.save')}
          </Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-ADV-03: split the exact sellable pieces into lots; permanent (owner, PRD-09, ADR-120). */
export function OptInLotsSheet({ product, onClose }: { product: Product; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const places = useQuery({ queryKey: [...LOTS, 'places', product.id], queryFn: () => loadProductPlaces(product.id) });
  const sellable = (places.data ?? []).reduce((s, r) => s + r.pieces, 0);
  const [lots, setLots] = useState<{ lot: string; expiry: string; pieces: string }[]>([
    { lot: '', expiry: '', pieces: '' },
  ]);
  const [problem, setProblem] = useState('');
  const split = lots.reduce((s, l) => s + (Number(l.pieces) || 0), 0);
  const save = useMutation({
    mutationFn: () =>
      optInLots(
        product.id,
        lots
          .filter((l) => Number(l.pieces) > 0)
          .map((l) => ({ lot: l.lot.trim(), expiry: l.expiry, pieces: Number(l.pieces) })),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOG });
      onClose();
    },
    onError: (e) => setProblem(refusal(e)),
  });
  const edit = (i: number, k: 'lot' | 'expiry' | 'pieces', v: string) =>
    setLots(lots.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const used = lots.filter((l) => Number(l.pieces) > 0);
    if (used.some((l) => !l.lot.trim())) return setProblem(t('err.lotno'));
    if (used.some((l) => !l.expiry)) return setProblem(t('err.lotexp'));
    const dup = used.find((l, i) => used.findIndex((x) => x.lot.trim() === l.lot.trim()) !== i);
    if (dup) return setProblem(t('err.lotdup', { l: dup.lot.trim() }));
    if (split !== sellable) return setProblem(t('err.lotsum', { a: split, b: sellable }));
    setProblem('');
    save.mutate();
  };
  return (
    <BottomSheet title={`${t('lot.track')} · ${product.name}`} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        <Notice tone="danger">{t('lot.perm')}</Notice>
        <p className="muted">{t('lot.tosplit', { n: sellable })}</p>
        <List label={t('tab.lots')}>
          {lots.map((l, i) => (
            <ListRow
              key={i}
              testId={`lotrow-${i}`}
              title={
                <span className="yv-skeleton-stack">
                  <FormGrid>
                    <Field label={t('lot.no')}>
                      {({ id }) => (
                        <Input id={id} dir="ltr" value={l.lot} onChange={(e) => edit(i, 'lot', e.target.value)} />
                      )}
                    </Field>
                    <Field label={t('lot.expiry')}>
                      {({ id }) => (
                        <Input
                          id={id}
                          type="date"
                          dir="ltr"
                          value={l.expiry}
                          onChange={(e) => edit(i, 'expiry', e.target.value)}
                        />
                      )}
                    </Field>
                  </FormGrid>
                  <Field label={t('lot.pieces')}>
                    {({ id }) => (
                      <Input
                        id={id}
                        numeric
                        inputMode="numeric"
                        value={l.pieces}
                        onChange={(e) => edit(i, 'pieces', e.target.value)}
                      />
                    )}
                  </Field>
                </span>
              }
              actions={
                lots.length > 1 ? (
                  <Button
                    pill
                    icon="close"
                    aria-label={t('remove')}
                    onClick={() => setLots(lots.filter((_, j) => j !== i))}
                  />
                ) : undefined
              }
            />
          ))}
        </List>
        <Button icon="plus" onClick={() => setLots([...lots, { lot: '', expiry: '', pieces: '' }])}>
          {t('lot.add')}
        </Button>
        <Notice tone={split === sellable ? 'info' : 'warning'}>{t('lot.split', { a: split, b: sellable })}</Notice>
        {problem && <Notice tone="danger">{problem}</Notice>}
        <SheetActions>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('lot.start')}
          </Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

/** SCR-ADV-04: lots in sale order with their expiry badges; expired lots can be recorded (ADR-123). */
export function LotsTab({ product }: { product: Product }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const { role } = useSession();
  const lots = useQuery({ queryKey: [...LOTS, product.id], queryFn: () => loadLots(product.id) });
  const [expiring, setExpiring] = useState<Lot | null>(null);
  if (!lots.data) return <SkeletonList label={t('loading')} />;
  const live = lots.data.filter((l) => l.pieces > 0);
  const used = lots.data.filter((l) => l.pieces === 0);
  if (live.length === 0 && used.length === 0)
    return <EmptyState icon="tag" title={t('lot.empty')} text={t('lot.empty.text')} />;
  const nearest = live.find((l) => !l.expired && l.expiry);
  const expiredPieces = live.filter((l) => l.expired).reduce((s, l) => s + l.pieces, 0);
  const canExpire = role === 'owner' || role === 'storekeeper';
  return (
    <>
      <SummaryCard
        label={t('lot.sum')}
        value={t('piece.n', { n: live.reduce((s, l) => s + l.pieces, 0) })}
        sub={nearest?.expiry ? `${t('lot.nearest')} ${formatDate(nearest.expiry, lang)}` : undefined}
        figures={[
          { label: t('lot.count'), value: String(live.length) },
          { label: t('lot.expired'), value: t('piece.n', { n: expiredPieces }) },
        ]}
      />
      <p className="muted">{t('lot.fefo')}</p>
      <List label={t('tab.lots')}>
        {live.map((l) => (
          <ListRow
            key={l.id}
            testId={`lot-${l.lot_no}`}
            lead={{ icon: 'tag' }}
            title={<Code>{l.lot_no}</Code>}
            sub={
              <>
                {t('lot.expiry')} {l.expiry ? <bdi className="yv-nowrap">{formatDate(l.expiry, lang)}</bdi> : '—'}
                {l.expired ? (
                  <>
                    <br />
                    <Badge tone="danger">{t('lot.expiredb')}</Badge>
                  </>
                ) : l.days_left !== null && l.days_left <= 30 ? (
                  <>
                    <br />
                    <Badge tone="warning">{t('lot.soon', { n: l.days_left })}</Badge>
                  </>
                ) : null}
              </>
            }
            value={t('piece.n', { n: l.pieces })}
            actions={
              l.expired && canExpire ? (
                <Button pill onClick={() => setExpiring(l)}>
                  {t('ex.act')}
                </Button>
              ) : undefined
            }
          />
        ))}
      </List>
      {used.length > 0 && (
        <Section title={t('lot.used')}>
          <List label={t('lot.used')}>
            {used.map((l) => (
              <ListRow
                key={l.id}
                lead={{ icon: 'check' }}
                title={<Code>{l.lot_no}</Code>}
                sub={l.expiry ? `${t('lot.expiry')} ${formatDate(l.expiry, lang)}` : undefined}
                value={t('piece.n', { n: 0 })}
              />
            ))}
          </List>
        </Section>
      )}
      {expiring && <ExpireLotSheet lot={expiring} product={product} onClose={() => setExpiring(null)} />}
    </>
  );
}

/** SCR-ADV-05: the expired pieces leave the garage or a truck for the damaged pool, opening boxes if needed. */
export function ExpireLotSheet({ lot, product, onClose }: { lot: Lot; product: Product; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const qc = useQueryClient();
  const refusal = useRefusalText();
  const where = useQuery({ queryKey: [...LOTS, 'places', product.id], queryFn: () => loadProductPlaces(product.id) });
  const places = (where.data ?? []).filter((r) => r.pieces > 0);
  const [place, setPlace] = useState('');
  const [pieces, setPieces] = useState(String(lot.pieces));
  const [problem, setProblem] = useState('');
  const chosen = place || places[0]?.location_id || '';
  const save = useMutation({
    mutationFn: () => recordExpiredLot(lot.id, chosen, Number(pieces)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CATALOG });
      void qc.invalidateQueries({ queryKey: ['stock'] });
      onClose();
    },
    onError: (e) => setProblem(refusalCode(e) === 'INVALID_QUANTITY' ? t('err.exqty', { n: lot.pieces }) : refusal(e)),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = parseWhole(pieces);
    if (q === null || Number.isNaN(q) || q < 1 || q > lot.pieces) return setProblem(t('err.exqty', { n: lot.pieces }));
    if (!chosen) return setProblem(t('err.exnostock', { l: '' }));
    setProblem('');
    save.mutate();
  };
  return (
    <BottomSheet title={t('ex.title', { l: lot.lot_no })} open onClose={onClose} busy={save.isPending}>
      <form onSubmit={submit} noValidate className="yv-skeleton-stack">
        <List label={lot.lot_no}>
          <ListRow
            lead={{ icon: 'tag' }}
            title={<Name>{product.name}</Name>}
            sub={
              <>
                <Code>{lot.lot_no}</Code> · {t('lot.expiry')}{' '}
                <bdi className="yv-nowrap">{lot.expiry ? formatDate(lot.expiry, lang) : '—'}</bdi>
              </>
            }
            value={t('ex.left', { n: lot.pieces })}
            badge={<Badge tone="danger">{t('lot.expiredb')}</Badge>}
          />
        </List>
        <Field label={t('ex.from')}>
          {({ id }) => (
            <Select id={id} value={chosen} onChange={(e) => setPlace(e.target.value)}>
              {places.map((r) => (
                <option key={r.location_id} value={r.location_id}>
                  {r.kind === 'garage' ? t('loc.garage') : (r.truck_code ?? '')} — {t('piece.n', { n: r.pieces })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('ex.qty')} hint={t('ex.boxes')}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              numeric
              inputMode="numeric"
              value={pieces}
              aria-describedby={describedBy}
              onChange={(e) => setPieces(e.target.value)}
            />
          )}
        </Field>
        <Notice tone="info">{t('ex.goes')}</Notice>
        {problem && <Notice tone="danger">{problem}</Notice>}
        <SheetActions>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button type="submit" variant="main" busy={save.isPending}>
            {t('ex.save')}
          </Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}
